import { Download, Loader2, MoreHorizontal, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { downloadCsv, parseCsv, toCsv, type CsvColumn } from '../lib/csv';

/**
 * What to do with the rows somebody has ticked.
 *
 * ## Why it appears rather than sits there
 *
 * A toolbar that is always present is a row of buttons that are usually
 * disabled, and a disabled button teaches people not to look at that part of
 * the screen. This is absent until something is selected, and then it is the
 * only new thing on the page — so the first time somebody ticks a box, the
 * answer to "now what?" is already in front of them.
 *
 * ## Why the count is the first thing on it
 *
 * Because a selection can outlive the page it was made on. Ticking three rows,
 * filtering, and then pressing Archive is a real way to act on rows you can no
 * longer see — so the bar says how many are chosen, not how many are visible,
 * and the number is the part somebody reads before pressing anything.
 *
 * ## Why destructive things are behind the menu
 *
 * Export is safe and reversible and sits in the open. Archiving forty tickets
 * is neither, and a button of that weight beside a harmless one gets pressed
 * by accident. The extra click is the point.
 */

export type BulkAction<Row> = {
  key: string;
  label: string;
  icon?: typeof Download;
  /** Shown in the open rather than under the menu. For the harmless ones. */
  prominent?: boolean;
  /** Red, and asks first. */
  destructive?: boolean;
  /** What to ask before doing it. Skipped when absent. */
  confirm?: (rows: Row[]) => string;
  run: (rows: Row[]) => Promise<unknown> | unknown;
};

export function SelectionBar<Row>({
  rows,
  selected,
  onClear,
  rowKey,
  csv,
  exportName,
  onImport,
  actions = [],
}: {
  /** Everything on the page, so a selection can be resolved to real rows. */
  rows: Row[];
  selected: Set<string>;
  onClear: () => void;
  rowKey: (row: Row) => string;
  /** Columns for the export. Omit and the export button is not offered. */
  csv?: CsvColumn<Row>[];
  exportName?: string;
  /** Given the parsed file. Omit and no import is offered. */
  onImport?: (records: Array<Record<string, string>>) => Promise<unknown> | unknown;
  actions?: BulkAction<Row>[];
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);

  /*
   * The menu does not survive the selection emptying.
   *
   * Clearing the selection hides the bar but does not unmount it, so `open`
   * stayed true underneath. Selecting again brought the bar back with its menu
   * already showing — which nobody asked for, and which made the next press of
   * the button close it instead of opening it. It looked exactly like a button
   * that does nothing.
   */
  useEffect(() => {
    if (selected.size === 0) setOpen(false);
  }, [selected.size]);

  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (menu.current && !menu.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  /*
   * Only the rows actually on this page.
   *
   * A selection can name a row that has since been filtered away, and an
   * action that silently did nothing to it would be worse than one that says
   * how many it touched. The bar counts the selection; the actions get what
   * can be resolved.
   */
  const chosen = rows.filter((row) => selected.has(rowKey(row)));

  if (selected.size === 0) return null;

  const perform = async (action: BulkAction<Row>) => {
    setOpen(false);
    if (action.confirm && !window.confirm(action.confirm(chosen))) return;

    setBusy(action.key);
    try {
      await action.run(chosen);
    } finally {
      setBusy(null);
    }
  };

  const exportRows = () => {
    if (!csv) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`${exportName ?? 'export'}-${stamp}.csv`, toCsv(chosen, csv));
    setOpen(false);
  };

  const readFile = async (chosenFile: File) => {
    if (!onImport) return;
    setBusy('import');
    try {
      const records = parseCsv(await chosenFile.text());
      await onImport(records);
    } finally {
      setBusy(null);
      // Cleared, or choosing the same file twice does nothing the second time.
      if (file.current) file.current.value = '';
    }
  };

  const inTheOpen = actions.filter((one) => one.prominent);
  const underTheMenu = actions.filter((one) => !one.prominent);

  return (
    <div
      /* A stable hook. The bar is identified by its text otherwise, and every
         ancestor up to <body> contains that text too. */
      data-selection-bar=""
      /* Never printed: it is a control strip, and on paper it would sit across
         the top of the first page instead of the rows somebody wanted. */
      className="sticky top-0 z-20 -mx-1 mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-primary-300 bg-primary-50 px-3 py-2 print:hidden"
    >
      <span className="text-sm font-medium text-primary-900">
        {selected.size} selected
        {chosen.length !== selected.size && (
          <span className="font-normal text-primary-700">
            {' '}
            · {chosen.length} on this page
          </span>
        )}
      </span>

      <div className="ml-auto flex flex-wrap items-center gap-1.5">
        {csv && (
          <button type="button" className="btn-secondary" onClick={exportRows}>
            <Download className="h-4 w-4" aria-hidden="true" />
            Export CSV
          </button>
        )}

        {inTheOpen.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.key}
              type="button"
              className="btn-secondary"
              disabled={busy !== null}
              onClick={() => void perform(action)}
            >
              {busy === action.key ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                Icon && <Icon className="h-4 w-4" aria-hidden="true" />
              )}
              {action.label}
            </button>
          );
        })}

        {(underTheMenu.length > 0 || onImport) && (
          <div ref={menu} className="relative">
            <button
              type="button"
              className="btn-secondary"
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="More actions"
              onClick={() => setOpen((on) => !on)}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </button>

            {open && (
              <div
                role="menu"
                className="absolute right-0 top-full z-30 mt-1 w-56 rounded-xl border border-border bg-card p-1 shadow-lg"
              >
                {onImport && (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted"
                    onClick={() => {
                      setOpen(false);
                      file.current?.click();
                    }}
                  >
                    <Upload className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Import from CSV…
                  </button>
                )}

                {underTheMenu.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.key}
                      type="button"
                      role="menuitem"
                      disabled={busy !== null}
                      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted disabled:opacity-50 ${
                        action.destructive ? 'text-danger-700' : ''
                      }`}
                      onClick={() => void perform(action)}
                    >
                      {busy === action.key ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                      ) : (
                        Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      )}
                      {action.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="btn-quiet"
          onClick={onClear}
          aria-label="Clear the selection"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/*
        Hidden, and driven by the menu item above. A bare file input is one of
        the few controls no amount of CSS makes presentable, and it does not
        need to be visible to work.
      */}
      {onImport && (
        <input
          ref={file}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(event) => {
            const picked = event.target.files?.[0];
            if (picked) void readFile(picked);
          }}
        />
      )}
    </div>
  );
}
